import { sub } from "date-fns";
import { Feature, FeatureCollection, GeoJsonProperties, Geometry } from "geojson";
import { FunctionComponent, useCallback, useMemo, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import LeftIcon from "@mui/icons-material/West";

import { LayerSettingValueSet } from "@/lib/map-layers/layer-settings";
import useSelectedMarker from "@/features/shared/widgets/map/settings/use-selected-marker";
import { SeismicEventsMapLayer as SeismicEventsMapLayerConfig } from "@/lib/api";
import OverlayControl from "@/features/shared/widgets/map/controls/overlay-control";
import { OverlayControlHeader } from "@/features/shared/widgets/map/controls/overlay-control.styles";
import SeismicEventsOverlay from "@/features/shared/widgets/map/layers/seismic-events-layer/seismic-events-overlay";
import { SeismicEventDateOptions } from "@/features/shared/widgets/map/map.types";
import { useTranslation } from "@/lib/i18n";

import SeismicEventsMarker from "./seismic-events-marker";

interface SeismicEventsMarkersProps {
  data: FeatureCollection;
  layer: SeismicEventsMapLayerConfig;
  currentSettingValues: LayerSettingValueSet;
}

const SeismicEventsMarkers: FunctionComponent<SeismicEventsMarkersProps> = ({
  data,
  layer,
  currentSettingValues,
}) => {
  const { t } = useTranslation();
  const { selectedMarker, setSelectedMarker, resetSelectedMarker } = useSelectedMarker();
  const [expanded, setExpanded] = useState(true);

  const availableEvents = useMemo(() => {
    const { seismicEventsHistory } = currentSettingValues;
    let timeBack: Duration;
    switch (seismicEventsHistory?.value) {
      case SeismicEventDateOptions.ONE_DAY:
        timeBack = {
          days: 1,
        };
        break;
      case SeismicEventDateOptions.ONE_MONTH:
        timeBack = {
          months: 1,
        };
        break;
      case SeismicEventDateOptions.TWO_MONTHS:
        timeBack = {
          months: 2,
        };
        break;
      default:
      case SeismicEventDateOptions.THREE_MONTHS:
        timeBack = {
          months: 3,
        };
    }

    const maxStartDate = sub(new Date(), timeBack);

    return data.features.filter((feature) => {
      if (!feature.properties) return false;
      return new Date(feature.properties.time) > maxStartDate;
    });
  }, [data, currentSettingValues]);

  const selectedFeature = useMemo(() => {
    if (!selectedMarker) return undefined;
    if (selectedMarker.layerId !== layer.id) return undefined;

    return availableEvents.find((feature) => feature.id === selectedMarker.value);
  }, [layer, availableEvents, selectedMarker]);

  const handleSelect = useCallback(
    (feature: Feature<Geometry, GeoJsonProperties>) => {
      if (!feature.id) return;
      setSelectedMarker(feature.id.toString(), layer.id);
    },
    [setSelectedMarker, layer],
  );

  return (
    <>
      {availableEvents.map((feature) => (
        <SeismicEventsMarker
          key={feature.id}
          feature={feature}
          selected={selectedFeature?.id === feature.id}
          onSelect={handleSelect}
          currentSettingValues={currentSettingValues}
        />
      ))}

      {(!selectedMarker || selectedMarker.layerId === layer.id) && (
        <OverlayControl>
          <OverlayControlHeader>
            {expanded ? (
              <>
                <span>
                  Showing{" "}
                  {t(
                    `map.seismic_events.dates.${
                      currentSettingValues.seismicEventsHistory
                        .value as SeismicEventDateOptions
                    }`,
                  )}{" "}
                  history
                </span>
                <CloseIcon
                  onClick={() => {
                    resetSelectedMarker();
                    setExpanded(false);
                  }}
                />
              </>
            ) : (
              <LeftIcon onClick={() => setExpanded(true)} />
            )}
          </OverlayControlHeader>

          {expanded && (
            <SeismicEventsOverlay
              availableEvents={availableEvents}
              currentSettingValues={currentSettingValues}
              handleSelect={handleSelect}
              handleClose={resetSelectedMarker}
            />
          )}
        </OverlayControl>
      )}
    </>
  );
};

export default SeismicEventsMarkers;
